import { Link, createFileRoute } from '@tanstack/react-router'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { DEFAULT_CHAPTER } from '~/lib/adb/client'
import { useChapter, useChapterEvents, useRegions } from '~/lib/adb/chapter'
import { loadRegions, loadSeed } from '~/lib/adb/events-loader'
import { buttonClass } from '~/components/ui/Button'
import { Container } from '~/components/ui/Container'
import { ChapterBar } from '~/components/events/ChapterBar'
import { EventList } from '~/components/events/EventList'
import { FeaturedEvent } from '~/components/events/FeaturedEvent'
import { SignupPitch } from '~/components/SignupPitch'
import { PageTitle } from '~/components/ui/PageTitle'
import { PageHero } from '~/components/ui/PageHero'

export const Route = createFileRoute('/events/')({
  loader: async ({ context: { queryClient } }) => {
    const [seed, regions] = await Promise.all([
      loadSeed(queryClient, DEFAULT_CHAPTER.ID),
      loadRegions(queryClient),
    ])
    return { seed, regions }
  },
  headers: () => cacheHeaders.events,
  head: () =>
    pageMeta({
      title: 'Events',
      description:
        'Find animal rights events near you: protests, vigils, rescues, trainings and more from DxE chapters around the world.',
      ogDescription: 'Find animal rights events near you.',
    }),
  component: EventsPage,
})

function EventsPage() {
  const { seed, regions } = Route.useLoaderData()
  // Puts the prerendered regions in the query cache for the chapter picker.
  useRegions(regions)
  const { chapter, resolved } = useChapter()
  const { upcoming, failed } = useChapterEvents(chapter.ID, seed, resolved)
  const featured = upcoming?.filter((e) => e.Featured) ?? []

  return (
    <>
      <PageHero bottom="section">
        <Container>
          <PageTitle className="mb-5 text-center">
            Find Events Near You
          </PageTitle>
          <div className="flex flex-col gap-5">
            {featured.map((ev) => (
              <FeaturedEvent key={ev.ID} event={ev} pageId={chapter.ID} />
            ))}
          </div>
        </Container>
      </PageHero>

      <section className="pt-[25px] pb-[50px] md:pb-20">
        <Container width="wide">
          <ChapterBar />
          <EventList events={upcoming} failed={failed} chapter={chapter} />
          <div className="flex justify-center pt-10">
            <Link to="/events/chapters" className={buttonClass('outline')}>
              Browse events by chapter
            </Link>
          </div>
        </Container>
      </section>

      <section className="bg-surface-alt py-[50px] md:py-20">
        <Container className="max-w-[850px]">
          <SignupPitch title="Join our network" className="md:grid-cols-2" />
        </Container>
      </section>
    </>
  )
}
